import React, { useCallback, useMemo, useState } from 'react';

import { Link, useParams } from 'react-router-dom';

import '../assets/styles/loan/Loan.scss';
import { renderBnplLayout } from '../components/Bnpl/BnplLayout';
import { Breadcrumb, BreadcrumbItem } from '../components/Breadcrumb';
import LoanBanner from '../components/LoanBanner';
import LoanStatementDetails from '../components/LoanStatementDetails';
import LoanStatements from '../components/LoanStatements';
import Layout from '../components/common/Layout';
import configData from '../config';
import { useLoanDetails } from '../hooks/useLoanDetails';
import { useLoanStatements } from '../hooks/useLoanStatements';
import { getStatementDetails } from '../service/loan';

function showFile(blob, statement) {
  // It is necessary to create a new blob object with mime-type explicitly set
  // otherwise only Chrome works like it should
  const newBlob = new Blob([blob], { type: 'application/pdf' });

  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(newBlob);
    return;
  }

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const data = window.URL.createObjectURL(newBlob);
  var link = document.createElement('a');
  link.href = data;
  link.download = `loan-statement-${statement}.pdf`;
  link.click();
  setTimeout(function () {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(data);
  }, 100);
}

const LoanDetailsPage = () => {
  const [selectedStatementId, setSelectedStatement] = useState(null);
  const params = useParams();
  const { loan, loanLoading } = useLoanDetails();
  const { loanStatements, loanStatementsLoading } = useLoanStatements();
  const isBnpl = configData.getTenant() === 'lightrx';

  const selectedStatement = useMemo(
    () => loanStatements && loanStatements.find((s) => s.statementRowId === selectedStatementId),
    [loanStatements, selectedStatementId]
  );

  const onDownload = useCallback(async () => {
    const { data } = await getStatementDetails(params.id, selectedStatementId);
    showFile(data, selectedStatementId);
  }, [params.id, selectedStatementId]);

  const renderLoanStatement = () => {
    return selectedStatement ? (
      <LoanStatementDetails
        data={selectedStatement}
        downloadStatement={onDownload}
        onBack={resetSelectedStatement}
      />
    ) : (
      <LoanStatements
        data={loanStatements}
        loanId={params.id}
        selectStatement={setSelectedStatement}
      />
    );
  };

  const resetSelectedStatement = useCallback(() => setSelectedStatement(null), []);
  if (isBnpl) {
    return renderBnplLayout(
      <Layout>
        {!loanStatementsLoading && !loanLoading && (
          <div className="loanSection ant-layout">
            <div className="loanContainer">
              <div className="block breadcrumb">
                <Breadcrumb>
                  <BreadcrumbItem>
                    <Link to="/dashboard">Dashboard</Link>
                  </BreadcrumbItem>
                  {loan && <BreadcrumbItem>Loan #{loan?.loanDetail?.acctrefno}</BreadcrumbItem>}
                </Breadcrumb>
              </div>
              <LoanBanner data={loan?.loanDetail} />
              {renderLoanStatement()}
            </div>
          </div>
        )}
      </Layout>
    );
  }
  return (
    <Layout>
      {!loanStatementsLoading && !loanLoading && (
        <div className="loanSection ant-layout">
          <div className="loanContainer">
            <div className="block breadcrumb">
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link to="/dashboard">Dashboard</Link>
                </BreadcrumbItem>
                {loan && <BreadcrumbItem>Loan #{loan?.loanDetail?.acctrefno}</BreadcrumbItem>}
              </Breadcrumb>
            </div>
            <LoanBanner data={loan?.loanDetail} />
            {renderLoanStatement()}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default LoanDetailsPage;
